import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import MobileShell from '../../components/MobileShell';
import {
  ArrowLeft, MessageSquare, Send, Loader2, Clock, User as UserIcon, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITIES = {
  high:   { label: 'High',   dot: 'bg-red-500',   chip: 'text-red-700' },
  normal: { label: 'Medium', dot: 'bg-amber-500', chip: 'text-amber-700' },
  low:    { label: 'Low',    dot: 'bg-slate-400', chip: 'text-slate-500' },
};

/**
 * MobileTaskDetail — dedicated page (was a bottom-sheet modal).
 * Route: /m/tasks/:id
 */
export default function MobileTaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/tasks/${id}`).then(r => setTask(r.data)).catch(() => {
    // Fallback: fetch list and find
    return api.get('/tasks').then(r => {
      const t = (r.data || []).find(x => x.id === id);
      if (t) setTask(t); else toast.error('Task not found');
    });
  });

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  const postNote = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tasks/${id}/notes`, { body: note.trim() });
      setNote('');
      await load();
    } catch { toast.error('Failed'); }
    finally { setPosting(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/tasks/${id}`, { status });
      toast.success(status === 'done' ? 'Marked done' : `Marked ${status.replace('_', ' ')}`);
      await load();
    } catch { toast.error('Failed'); }
  };

  if (loading || !task) {
    return (
      <MobileShell noHeader>
        <div className="pt-3 pb-2 flex items-center gap-2">
          <button
            onClick={() => nav(-1)}
            className="w-10 h-10 rounded-full bg-white/85 border border-white/70 grid place-items-center shadow-sm"
          >
            <ArrowLeft size={16}/>
          </button>
          <div className="text-xs text-slate-500">Loading…</div>
        </div>
        {loading && <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={22}/></div>}
        {!loading && !task && (
          <div className="pt-8 text-center">
            <Link to="/m/tasks" className="text-[#679286] text-sm font-medium">← Back to tasks</Link>
          </div>
        )}
      </MobileShell>
    );
  }

  const p = PRIORITIES[task.priority] || PRIORITIES.normal;

  return (
    <MobileShell noHeader>
      {/* Header */}
      <div className="pt-3 pb-2 flex items-center gap-2" data-testid="m-task-detail-page">
        <button
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-full bg-white/85 border border-white/70 grid place-items-center shadow-sm"
          data-testid="m-task-back"
        >
          <ArrowLeft size={16}/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Task</div>
          <h1 className="font-serif text-lg leading-tight truncate">{task.title}</h1>
        </div>
      </div>

      <div className="space-y-4 pb-8">
        {/* Meta pill row */}
        <div className="flex flex-wrap gap-2 text-xs">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/85 border border-white/70 font-medium ${p.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`}/> {p.label}
          </span>
          {task.due_date && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/85 border border-white/70 text-slate-600">
              <Clock size={11}/> {new Date(task.due_date).toLocaleDateString()}
            </span>
          )}
          {task.assignee_name && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/85 border border-white/70 text-slate-600">
              <UserIcon size={11}/> {task.assignee_name}
            </span>
          )}
          {task.status === 'done' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
              <CheckCircle size={11}/> Done
            </span>
          )}
        </div>

        {task.description && (
          <div className="glass p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Description</div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{task.description}</p>
          </div>
        )}

        {/* Status quick actions */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 px-1">Status</div>
          <div className="grid grid-cols-3 gap-2">
            {['open', 'in_progress', 'done'].map(s => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`m-task-status-${s}`}
                className={`py-3 rounded-2xl text-xs font-semibold uppercase tracking-widest active:scale-[0.97] transition-transform ${
                  task.status === s ? 'bg-[#1A2530] text-white shadow-md' : 'bg-white/85 border border-white/70 text-slate-600'
                }`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Trail */}
        <div className="glass p-4 text-xs text-slate-500 space-y-1">
          {task.assigned_by_name && <div>Created by <b className="text-slate-700">{task.assigned_by_name}</b></div>}
          {task.assignee_name && <div>Assigned to <b className="text-slate-700">{task.assignee_name}</b></div>}
          {task.property_id && <div>Property <Link to={`/m/properties/${task.property_id}`} className="text-[#679286] font-medium">Open →</Link></div>}
        </div>

        {/* Progress notes */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 px-1 flex items-center gap-1">
            <MessageSquare size={11}/> Progress notes ({task.notes?.length || 0})
          </div>
          <div className="space-y-2 mb-3">
            {(!task.notes || task.notes.length === 0) && (
              <div className="glass p-4 text-xs text-slate-400 italic text-center">No notes yet.</div>
            )}
            {(task.notes || []).slice().reverse().map(n => (
              <div key={n.id} className="p-3 rounded-2xl bg-white/85 border border-white/70">
                <div className="text-[11px] text-slate-500 mb-1">
                  <b className="text-slate-700">{n.author_name || 'Someone'}</b>
                  {n.created_at && <span className="ml-2">{new Date(n.created_at).toLocaleDateString()}</span>}
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a progress note…"
              data-testid="m-task-note-input"
              className="flex-1 px-4 py-3 rounded-2xl bg-white/85 border border-white/70 text-sm outline-none focus:ring-2 focus:ring-[#679286]/30"
            />
            <button
              onClick={postNote}
              disabled={!note.trim() || posting}
              data-testid="m-task-note-submit"
              className="w-12 h-12 rounded-2xl bg-[#679286] text-white grid place-items-center disabled:opacity-40 active:scale-[0.95] transition-transform"
            >
              <Send size={16}/>
            </button>
          </div>
        </div>
      </div>
    </MobileShell>
  );
}
